import { enUS, plPL } from "@clerk/localizations";
import { ClerkProvider } from "@clerk/react-router";
import { clerkMiddleware, rootAuthLoader } from "@clerk/react-router/server";
import * as Sentry from "@sentry/react";
import { posthog } from "posthog-js";
import { useEffect } from "react";
import * as ReactRouter from "react-router";
import type { Route } from "../.react-router/types/app/+types/root";
import { CrispChat } from "./components/crisp-chat";
import { ErrorPage } from "./components/error-page";
import { LOCALE_COOKIE_NAME } from "./components/language-picker";
import { NavigationProgress } from "./components/navigation-progress";
import { PosthogUser } from "./components/posthog";
import { SentryUser } from "./components/sentry-user";
import { Toaster } from "./components/ui/toaster";
import * as Paths from "./paths";
import stylesheet from "./tailwind.css?url";

type Locale = "pl" | "en";

// Parse locale from cookie or Accept-Language header
function getLocaleFromRequest(request: Request): Locale {
  // First, check for locale cookie
  const cookieHeader = request.headers.get("Cookie") || "";
  const cookies = Object.fromEntries(
    cookieHeader.split("; ").map((c) => {
      const [key, ...val] = c.split("=");
      return [key, val.join("=")];
    })
  );

  const cookieLocale = cookies[LOCALE_COOKIE_NAME];
  if (cookieLocale === "pl" || cookieLocale === "en") {
    return cookieLocale;
  }

  // Fall back to Accept-Language header
  const acceptLanguage = request.headers.get("Accept-Language") || "";
  if (acceptLanguage.toLowerCase().includes("pl")) {
    return "pl";
  }

  return "en";
}

export const meta: ReactRouter.MetaFunction = () => [{ title: "AnyLawyer" }];

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap",
  },
];

export const middleware: Route.MiddlewareFunction[] = [clerkMiddleware()];

export async function loader(args: Route.LoaderArgs) {
  const locale = getLocaleFromRequest(args.request);

  return rootAuthLoader(args, async () => {
    return {
      locale,
      config: {
        PARROT_ENV: process.env.PARROT_ENV,
        CLERK_ACCOUNT_PORTAL_URL: process.env.CLERK_ACCOUNT_PORTAL_URL,
      },
    };
  });
}

export default function App({ loaderData }: Route.ComponentProps) {
  const _location = ReactRouter.useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, []);

  // Use locale from loader (server-detected) or fallback to "en"
  const locale = loaderData?.locale || "en";
  const clerkLocalization = locale === "pl" ? plPL : enUS;

  return (
    <html lang={locale}>
      <head>
        <ReactRouter.Meta />
        <ReactRouter.Links />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
      </head>
      <body>
        <NavigationProgress />
        <ClerkProvider
          loaderData={loaderData}
          signInUrl={Paths.login()}
          signUpUrl={Paths.index()}
          signUpFallbackRedirectUrl={Paths.app()}
          signInFallbackRedirectUrl={Paths.app()}
          localization={clerkLocalization}
        >
          <SentryUser />
          <PosthogUser />
          <CrispChat />
          <ReactRouter.Outlet />
          <Toaster />
          <ReactRouter.ScrollRestoration />
          <ReactRouter.Scripts />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.PARROT_CONFIG = ${JSON.stringify(loaderData?.config ?? {})};`,
            }}
          />
        </ClerkProvider>
      </body>
    </html>
  );
}

export function ErrorBoundary({ error, loaderData }: Route.ErrorBoundaryProps) {
  const is404 = ReactRouter.isRouteErrorResponse(error) && error.status === 404;

  if (!is404 && error && error instanceof Error) {
    Sentry.captureException(error);
  }

  const locale = loaderData?.locale || "en";
  const stack =
    import.meta.env.DEV && !is404 && error instanceof Error
      ? error.stack
      : undefined;

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <ReactRouter.Meta />
        <ReactRouter.Links />
      </head>
      <body>
        <ErrorPage is404={is404} stack={stack} locale={locale} />
        <ReactRouter.ScrollRestoration />
        <ReactRouter.Scripts />
      </body>
    </html>
  );
}
